/**
 * Batch decryption service for optimized performance
 * Reduces N+1 decrypt calls to single batch requests
 */

import { supabase } from '@/integrations/supabase/client';
import { isEncryptionHealthy } from './encryption-health-check';
import { 
  getCachedDecrypted, 
  setCachedDecrypted, 
  hasCachedDecrypted 
} from './decryption-cache';
import { logger } from '@/utils/logger';
import type { EncryptedData } from '@/types/encryption';

interface BatchDecryptItem {
  encrypted: string;
  nonce: string;
  index: number;
}

interface BatchDecryptResult {
  index: number;
  decrypted: string | null;
  error?: string;
}

interface BatchDecryptResponse {
  results: BatchDecryptResult[];
  success: boolean;
  count: number;
  duration: number;
}

const MAX_BATCH_SIZE = 100;

/**
 * Decrypt multiple fields in a single Edge Function call
 * Uses in-memory cache to avoid re-decryption
 */
export const decryptBatch = async (
  items: EncryptedData[]
): Promise<string[]> => {
  if (items.length === 0) return [];

  const startTime = performance.now();
  
  // Check encryption health
  if (!isEncryptionHealthy()) {
    logger.error('[BatchDecrypt] ❌ Encryption not healthy');
    throw new Error('Encryption system not ready');
  }

  // Separate cached vs uncached items
  const results: (string | null)[] = new Array(items.length).fill(null);
  const uncachedItems: BatchDecryptItem[] = [];
  
  items.forEach((item, index) => {
    if (!item.encrypted || !item.nonce) {
      results[index] = '';
      return;
    }
    
    // Check cache first
    const cached = getCachedDecrypted<string>(item.encrypted, item.nonce);
    if (cached !== null) {
      results[index] = cached;
    } else {
      uncachedItems.push({
        encrypted: item.encrypted,
        nonce: item.nonce,
        index
      });
    }
  });

  // If all cached, return immediately
  if (uncachedItems.length === 0) {
    logger.debug(`[BatchDecrypt] ✅ All ${items.length} items from cache in ${(performance.now() - startTime).toFixed(0)}ms`);
    return results as string[];
  }

  // Get session for auth
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    logger.error('[BatchDecrypt] ❌ No active session');
    throw new Error('No active session - please login');
  }

  // Process in chunks if needed
  const chunks: BatchDecryptItem[][] = [];
  for (let i = 0; i < uncachedItems.length; i += MAX_BATCH_SIZE) {
    chunks.push(uncachedItems.slice(i, i + MAX_BATCH_SIZE));
  }

  logger.debug(`[BatchDecrypt] Processing ${uncachedItems.length} uncached items in ${chunks.length} chunk(s)`);

  // Process all chunks
  for (const chunk of chunks) {
    try {
      const { data, error } = await supabase.functions.invoke<BatchDecryptResponse>('decrypt-batch', {
        body: { items: chunk },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        logger.error('[BatchDecrypt] ❌ Edge function error:', error.message);
        throw new Error(`Batch decryption failed: ${error.message}`);
      }

      if (!data?.results) {
        logger.error('[BatchDecrypt] ❌ Invalid response');
        throw new Error('Invalid batch decrypt response');
      }

      // Process results and cache them
      for (const result of data.results) {
        const originalItem = chunk.find(item => item.index === result.index);
        
        if (result.decrypted !== null) {
          results[result.index] = result.decrypted;
          
          // Cache successful decryptions
          if (originalItem) {
            setCachedDecrypted(originalItem.encrypted, originalItem.nonce, result.decrypted);
          }
        } else {
          // Handle decryption failure
          logger.warn(`[BatchDecrypt] ⚠️ Item ${result.index} failed:`, result.error);
          results[result.index] = '';
        }
      }
    } catch (chunkError) {
      logger.error('[BatchDecrypt] ❌ Chunk processing failed:', chunkError);
      
      // Fall back to empty strings for failed chunk
      for (const item of chunk) {
        if (results[item.index] === null) {
          results[item.index] = '';
        }
      }
    }
  }

  const duration = performance.now() - startTime;
  logger.info(`[BatchDecrypt] ✅ Decrypted ${items.length} items (${uncachedItems.length} new, ${items.length - uncachedItems.length} cached) in ${duration.toFixed(0)}ms`);

  return results as string[];
};

/**
 * Helper to decrypt description and amount for a transaction row
 */
export const decryptTransactionFields = async (
  rows: Array<{
    id: string;
    description_encrypted?: string | null;
    description_nonce?: string | null;
    amount_encrypted?: string | null;
    amount_nonce?: string | null;
    description?: string | null;
  }>
): Promise<Map<string, { description: string; amount: number }>> => {
  const result = new Map<string, { description: string; amount: number }>();
  
  if (rows.length === 0) return result;

  // Collect all encrypted fields
  const allItems: EncryptedData[] = [];
  const indexMap: { id: string; type: 'description' | 'amount' }[] = [];

  for (const row of rows) {
    // Description
    allItems.push({
      encrypted: row.description_encrypted || '',
      nonce: row.description_nonce || ''
    });
    indexMap.push({ id: row.id, type: 'description' });

    // Amount
    allItems.push({
      encrypted: row.amount_encrypted || '',
      nonce: row.amount_nonce || ''
    });
    indexMap.push({ id: row.id, type: 'amount' });
  }

  // Batch decrypt all fields
  const decrypted = await decryptBatch(allItems);

  // Map results back to rows
  for (let i = 0; i < indexMap.length; i++) {
    const { id, type } = indexMap[i];
    const value = decrypted[i];

    if (!result.has(id)) {
      result.set(id, { description: '', amount: 0 });
    }

    const entry = result.get(id)!;
    if (type === 'description') {
      entry.description = value || '';
    } else {
      entry.amount = value ? parseFloat(value) : 0;
    }
  }

  return result;
};

/**
 * Helper to decrypt name fields for categories, accounts, cards, etc.
 */
export const decryptNameFields = async (
  rows: Array<{
    id: string;
    name_encrypted?: string | null;
    name_nonce?: string | null;
    name?: string | null;
  }>
): Promise<Map<string, string>> => {
  const result = new Map<string, string>();
  
  if (rows.length === 0) return result;

  const items: EncryptedData[] = rows.map(row => ({
    encrypted: row.name_encrypted || '',
    nonce: row.name_nonce || ''
  }));

  const decrypted = await decryptBatch(items);

  rows.forEach((row, index) => {
    result.set(row.id, decrypted[index] || row.name || '');
  });

  return result;
};
